import { Dashboard, Order } from '@apis'
import { GridCard, Status, Table, TableActions, TableUser } from '@components'
import AssignModal from '@components/alert/driverAssignModal'
import { IGridItem } from '@components/gridCard'
import { StyleColumn } from '@components/styles'
import TableDate from '@components/table/date'
import { Actions, store } from '@store/index'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { Fragment } from 'react/jsx-runtime'
import OrderManagementFilter, {
  IOrderFilter,
  validKeys,
} from './components/filter'
import { IOrderManagementParams } from './types'
import DriveStatus from '@components/status/driverStatus'
import PopupToast from '@components/custom-toast'
import { modalConfig } from '../../../utils/modalConfig'

const AdminOrderManagement = ({ hasStat = true }) => {
  const [searchParams] = useSearchParams()
  const { data: dashboard } = useSelector((state: any) => state.orderDashboard)
  const tab = validKeys.includes(searchParams.get('tab') as IOrderFilter['key'])
    ? (searchParams.get('tab') as IOrderFilter['key'])
    : 'Self'
  const { data: user } = useSelector((state: any) => state.user)
  const [loading, setLoading] = useState<boolean>(false)
  const [driverPopup, setDriverPopup] = useState<boolean>(false)
  const [modalData, setModalData] = useState<any>({})
  const [visible, setVisible] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [order, setOrder] = useState<any>(null)
  const { items: orders, loading: fetching } = useSelector(
    (state: any) => state.orders,
  )

  const [params, setParams] = useState<IOrderManagementParams>({
    page: 1,
    limit: 10,
    status: null,
    adminId: null,
    search: null,
  })
  const fetchOrders = async (params: IOrderManagementParams) => {
    await Order.getAll('set', 'orders', params).catch(() => {})
  }
  const fetchDashboard = async () => {
    const params = {
      type: 'order',
    }
    await Dashboard.getAllDashboardCount('orderDashboard', params)
  }
  useEffect(() => {
    {
      hasStat && fetchDashboard()
    }
    if (!orders?.length && tab === 'Self') {
      const updatedParams = {
        ...params,
        adminId: user.id,
      }
      setParams(updatedParams)
      fetchOrders(updatedParams)
    }
    if (!orders?.length && tab === 'Lend') {
      const updatedParams: any = {
        ...params,
        adminId: null,
        status: 'Delivered',
      }
      setParams(updatedParams)
      fetchOrders(updatedParams)
    }
    if (!orders?.length && tab === 'Driver Assigned') {
      const updatedParams: any = {
        ...params,
        adminId: null,
        status: 'Driver Assigned',
      }
      setParams(updatedParams)
      fetchOrders(updatedParams)
    }
    if (!orders?.length && tab === 'Return') {
      const updatedParams: any = {
        ...params,
        adminId: null,
        status: 'Returned',
      }
      setParams(updatedParams)
      fetchOrders(updatedParams)
    }
  }, [])
  const items: IGridItem[] = [
    {
      title: 'Total Order',
      value: dashboard?.totalOrder || '0',
      Icon: 'book',
      IconBg: 'blueLight',
    },
    {
      title: 'Pending Book Request',
      value: dashboard?.totalPendingBook || '0',
      Icon: 'book',
      IconBg: 'redLight',
    },
    {
      title: 'Total Book Lend',
      value: dashboard?.totalLendBook || '0',
      Icon: 'book',
      IconBg: 'cyan100',
    },
    {
      title: 'Total Book Returned',
      value: dashboard?.totalReturnedBook || '0',
      Icon: 'book',
      IconBg: 'yellowLight',
    },
  ]
  const selfColumns = [
    {
      title: 'Book Name',
      key: 'selfName',
      render: (order: any) => (
        <TableUser
          bookCover
          avatar={order?.bookData?.coverImage}
          label={order?.bookData?.name}
          secondaryLabel={order?.bookData?.author}
        />
      ),
    },
    {
      title: 'Borrower',
      key: 'selfBorrower',
      render: (order: any) => (
        <TableUser
          hasAvatar
          avatar={order?.userCreated?.image}
          label={order?.userCreated?.name}
          secondaryLabel={order?.userCreated?.phoneNo}
        />
      ),
    },
    {
      title: 'Date',
      key: 'selfDate',
      render: (order: any) => (
        <TableDate date={order?.createdAt} hasTime={false} />
      ),
    },
    {
      title: 'Issued Date',
      key: 'selfIssuedDate',
      render: (order: any) => (
        <>
          {order?.issueDate ? (
            <TableDate date={order?.createdAt} hasTime={false} />
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      title: 'Return Date',
      key: 'selfReturnDate',
      render: (order: any) => (
        <>
          {order?.returnDate ? (
            <TableDate date={order?.createdAt} hasTime={false} />
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      title: 'Status',
      key: 'selfStatus',
      render: (order: any) => <Status status={order?.status} />,
    },
    {
      title: 'Delivery Status',
      key: 'deliveryStatus',
      render: (order: any) => {
        const driverStatus = order?.actionStatus?.lend
          ? order?.actionStatus?.lend
          : order?.returnDriver?.id
            ? 'Confirm Delivery'
            : 'Confirm Pickup'
        const handleDriverStatus = () => {
          const configData = modalConfig(order, 'Lend')
          setDriverPopup(configData.visible)
          setModalData(configData)
        }
        return (
          <DriveStatus status={driverStatus} onClick={handleDriverStatus} />
        )
      },
    },
    {
      title: 'Action',
      key: 'selfAction',
      render: (item: any) => (
        <>
          {item?.status === 'Pending' || item?.status === 'Delivered' ? (
            <TableActions
              hasCustom
              hasEdit={false}
              hasDelete={false}
              hasView={false}
              customTitle="Assign"
              onCustom={() => {
                setOrder(item)
                setVisible(true)
              }}
            />
          ) : (
            '-'
          )}
        </>
      ),
    },
  ]
  const pendingColumns = [
    {
      title: 'Book Name',
      key: 'name',
      render: (order: any) => (
        <TableUser
          bookCover
          avatar={order?.bookData?.coverImage}
          label={order?.bookData?.name}
          secondaryLabel={order?.bookData?.author}
        />
      ),
    },
    {
      title: 'Lender',
      key: 'lender',
      render: (order: any) => (
        <TableUser
          hasAvatar
          avatar={order?.bookData?.userCreated?.image}
          label={order?.bookData?.userCreated?.name}
          secondaryLabel={order?.bookData?.userCreated?.phoneNo}
        />
      ),
    },
    {
      title: 'Borrower',
      key: 'borrower',
      render: (order: any) => (
        <TableUser
          hasAvatar
          avatar={order?.bookData?.userCreated?.image}
          label={order?.userCreated?.name}
          secondaryLabel={order?.userCreated?.phoneNo}
        />
      ),
    },
    {
      title: 'Date',
      key: 'date',
      render: (order: any) => (
        <TableDate date={order?.createdAt} hasTime={false} />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (order: any) => <Status status={order?.status} />,
    },
    {
      title: 'Action',
      key: 'action',
      render: (item: any) => (
        <TableActions
          hasCustom
          hasEdit={false}
          hasDelete={false}
          hasView={false}
          customTitle="Assign"
          onCustom={() => {
            setOrder(item)
            setVisible(true)
          }}
        />
      ),
    },
  ]
  const lendColumns = [
    {
      title: 'Book Name',
      key: 'lendName',
      render: (order: any) => (
        <TableUser
          bookCover
          avatar={order?.bookData?.coverImage}
          label={order?.bookData?.name}
          secondaryLabel={order?.bookData?.author}
        />
      ),
    },
    {
      title: 'Lender',
      key: 'lendLender',
      render: (order: any) => (
        <TableUser
          hasAvatar
          avatar={
            order?.bookData?.userCreated?.image ??
            order?.bookData?.adminCreated?.image
          }
          label={
            order?.bookData?.userCreated?.name ??
            order?.bookData?.adminCreated?.name
          }
          secondaryLabel={order?.bookData?.userCreated?.phoneNo ?? null}
        />
      ),
    },
    {
      title: 'Borrower',
      key: 'lendBorrower',
      render: (order: any) => (
        <TableUser
          avatar={order?.userCreated?.image}
          label={order?.userCreated?.name}
          secondaryLabel={order?.userCreated?.phoneNo}
        />
      ),
    },
    {
      title: 'Expiry Date',
      key: 'lendDate',
      render: (order: any) => (
        <TableDate date={order?.returnDate} hasTime={false} />
      ),
    },
    {
      title: 'Driver',
      key: 'lendPickupDriver',
      render: (order: any) => (
        <TableUser
          label={order?.pickUpDriver?.name}
          secondaryLabel={order?.pickUpDriver?.phoneNo}
        />
      ),
    },
    {
      title: 'Status',
      key: 'lendStatus',
      render: (order: any) => <Status status={order?.status} />,
    },
    {
      title: 'Action',
      key: 'lendAction',
      render: (item: any) => (
        <TableActions
          hasCustom
          hasEdit={false}
          hasDelete={false}
          hasView={false}
          customTitle="Assign"
          onCustom={() => {
            setOrder(item)
            setVisible(true)
          }}
        />
      ),
    },
  ]
  const returnColumns = [
    {
      title: 'Book Name',
      key: 'returnName',
      render: (order: any) => (
        <TableUser
          bookCover
          avatar={order?.bookData?.coverImage}
          label={order?.bookData?.name}
          secondaryLabel={order?.bookData?.author}
        />
      ),
    },
    {
      title: 'Lender',
      key: 'returnLender',
      render: (order: any) => (
        <TableUser
          hasAvatar
          avatar={
            order?.bookData?.userCreated?.image ??
            order?.bookData?.adminCreated?.image
          }
          label={
            order?.bookData?.userCreated?.name ??
            order?.bookData?.adminCreated?.name
          }
          secondaryLabel={order?.bookData?.userCreated?.phoneNo ?? null}
        />
      ),
    },
    {
      title: 'Borrower',
      key: 'returnBorrower',
      render: (order: any) => (
        <TableUser
          hasAvatar
          avatar={order?.userCreated?.avatar}
          label={order?.userCreated?.name}
          secondaryLabel={order?.userCreated?.phoneNo}
        />
      ),
    },
    {
      title: 'Lend Date',
      key: 'returnLendDate',
      render: (order: any) => (
        <TableDate date={order?.issueDate} hasTime={false} />
      ),
    },
    {
      title: 'Returned Date',
      key: 'returnReturnDate',
      render: (order: any) => (
        <TableDate date={order?.returnDate} hasTime={false} />
      ),
    },

    {
      title: 'Pickup Driver',
      key: 'returnPickupDriver',
      render: (order: any) => (
        <TableUser
          label={order?.pickUpDriver?.name}
          secondaryLabel={order?.pickUpDriver?.phoneNo}
        />
      ),
    },
    {
      title: 'Return Driver',
      key: 'returnReturnDriver',
      render: (order: any) => (
        <TableUser
          label={order?.returnDriver?.name}
          secondaryLabel={order?.returnDriver?.phoneNo}
        />
      ),
    },
  ]
  const driveAssignColumns = [
    {
      title: 'Book Name',
      key: 'assignName',
      render: (order: any) => (
        <TableUser
          bookCover
          avatar={order?.bookData?.coverImage}
          label={order?.bookData?.name}
          secondaryLabel={order?.bookData?.author}
        />
      ),
    },
    {
      title: 'Lender',
      key: 'assignLender',
      render: (order: any) => (
        <TableUser
          hasAvatar
          avatar={order?.bookData?.userCreated?.image}
          label={
            order?.bookData?.userCreated?.name ??
            order?.bookData?.adminCreated?.name
          }
          secondaryLabel={order?.bookData?.userCreated?.phoneNo ?? null}
        />
      ),
    },
    {
      title: 'Borrower',
      key: 'assignBorrower',
      render: (order: any) => (
        <TableUser
          hasAvatar
          label={order?.userCreated?.name}
          avatar={order?.userCreated?.image}
          secondaryLabel={order?.userCreated?.phoneNo}
        />
      ),
    },
    {
      title: 'Lend Date',
      key: 'assignLendDate',
      render: (order: any) => (
        <TableDate date={order?.issueDate} hasTime={false} />
      ),
    },
    {
      title: 'Returned Date',
      key: 'assignReturnDate',
      render: (order: any) => (
        <TableDate date={order?.returnDate} hasTime={false} />
      ),
    },

    {
      title: 'Pickup Driver',
      key: 'assignPickupDriver',
      render: (order: any) => (
        <TableUser
          label={order?.pickUpDriver?.name ?? '-'}
          secondaryLabel={order?.pickUpDriver?.phoneNo}
        />
      ),
    },
    {
      title: 'Return Driver',
      key: 'assignReturnDriver',
      render: (order: any) => (
        <TableUser
          label={order?.returnDriver?.name ?? '-'}
          secondaryLabel={order?.returnDriver?.phoneNo}
        />
      ),
    },
  ]
  const onChangeFilter = async (filter: any) => {
    store.dispatch(Actions.reset('orders'))
    setIsFetching(true)
    let updatedStatus: IOrderManagementParams['status']
    let updatedAdminId: IOrderManagementParams['adminId']
    switch (filter) {
      case 'Pending':
        updatedAdminId = null
        updatedStatus = 'Pending'
        break
      case 'Lend':
        updatedAdminId = null
        updatedStatus = 'Delivered'
        break
      case 'Return':
        updatedAdminId = null
        updatedStatus = 'Returned'
        break
      case 'Driver Assigned':
        updatedAdminId = null
        updatedStatus = 'Driver Assigned'
        break
      case 'Self':
        updatedStatus = null
        updatedAdminId = user?.id
        break
      default:
        break
    }

    const updatedParams = {
      ...params,
      adminId: updatedAdminId,
      status: updatedStatus,
    }
    setParams(updatedParams)
    await fetchOrders(updatedParams)
      .catch(() => {})
      .finally(() => {
        setIsFetching(false)
      })
  }
  const onCancel = () => {
    setDriverPopup(false)
    setModalData({})
  }
  const handleConfirm = async () => {
    if (modalData?.payload) {
      setLoading(true)
      await Order.updateStatus(
        'orders',
        modalData?.payload,
        modalData?.item?.id,
      )
        .then(() => {
          setDriverPopup(false)
          setModalData({})
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false)
        })
    }
  }

  return (
    <Fragment>
      <StyleColumn>
        {hasStat && <GridCard items={items} />}
        <OrderManagementFilter
          onChangeFilter={onChangeFilter}
          activeKey="Self"
        />
        {tab === 'Self' && (
          <Table
            tableTitle="Order List"
            loading={fetching || isFetching}
            columns={selfColumns}
            data={orders}
            filter={{
              hasFilter: true,
              hasSearch: true,
            }}
          />
        )}
        {tab === 'Lend' && (
          <Table
            tableTitle="Order List"
            loading={fetching || isFetching}
            columns={lendColumns}
            data={orders}
            filter={{
              hasFilter: true,
              hasSearch: true,
            }}
          />
        )}
        {tab === 'Return' && (
          <Table
            tableTitle="Order List"
            loading={fetching || isFetching}
            columns={returnColumns}
            data={orders}
            filter={{
              hasFilter: true,
              hasSearch: true,
            }}
          />
        )}
        {tab === 'Pending' && (
          <Table
            tableTitle="Order List"
            loading={fetching || isFetching}
            columns={pendingColumns}
            data={orders}
            filter={{
              hasFilter: true,
              hasSearch: true,
            }}
          />
        )}
        {tab === 'Driver Assigned' && (
          <Table
            tableTitle="Order List"
            loading={fetching || isFetching}
            columns={driveAssignColumns}
            data={orders}
            filter={{
              hasFilter: true,
              hasSearch: true,
            }}
          />
        )}
      </StyleColumn>
      <AssignModal
        isLend={tab === 'Lend'}
        isPending={tab === 'Pending'}
        visible={visible}
        order={order}
        setVisible={setVisible}
        onCancel={() => {
          setOrder(null)
          setVisible(false)
        }}
      />
      <PopupToast
        loading={loading}
        cancelText="Cancel"
        buttonText="Confirm"
        onClose={onCancel}
        onClick={handleConfirm}
        image="deactivate"
        isOpen={driverPopup}
        content={modalData?.description}
      />
    </Fragment>
  )
}

export default AdminOrderManagement
